"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { TDHeaderProps } from "trimble-terra/TDHeader";
import type { TDNavigationData } from "@/lib/formatters/formatTDNavigation";
import { isExternalUrl } from "@/lib/url/localizeUrl";
import { myProfileBaseUrl } from "@/lib/config/environments";
import { useLogin } from "@/context/login/LoginContext";
import { isUserAuthenticated } from "@/context/login/reducer";
import { openAssistFlyout } from "@/lib/assist/assistFlyoutEvents";
import { handleTIDSignIn } from "@/lib/tid/handleTIDSignIn";
import { handleTIDSignOut } from "@/lib/tid/handleTIDSignOut";
import "./TrimbleDeveloperNavigation.css";
function TDHeaderPlaceholder() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="td-header-placeholder" aria-hidden="true" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const TDHeader = dynamic(() => import("trimble-terra/TDHeader").then(mod => mod.TDHeader), {
  ssr: false,
  loading: TDHeaderPlaceholder
});

/**
 * Client wrapper for the Trimble Developer application header.
 *
 * `TDHeader` (from trimble-terra) is built on Modus web components and uses
 * React hooks, so — like the repo's other terra wrappers (GlobalFooterClient,
 * SiteFooterClient) — it must live behind a "use client" boundary. It is also
 * lazy-loaded via `next/dynamic` so the Modus / terra chunk is only fetched on
 * a trimble-developer build. The root layout fetches `tdSiteNavigation` and
 * formats the props (see formatTDNavigation); LayoutComponent renders this
 * wrapper sitewide, in place of the global SiteNavigation, whenever
 * `NEXT_PUBLIC_SITE_MACHINE_NAME === "trimble-developer"`. `onSignIn`/
 * `onSignOut` default to the shared TID sign-in/out flows unless the caller
 * passes its own, and `user` is derived from the existing TID login context
 * (the same token check the rest of the app uses via `isUserAuthenticated`)
 * so the button reflects real auth state instead of always showing "Sign in".
 */
type TrimbleDeveloperHeaderProps = TDHeaderProps & Pick<TDNavigationData, "showAssistant" | "showThemeToggle" | "showNotifications" | "signInLink">;
export default function TrimbleDeveloperHeader({
  showAssistant = true,
  showThemeToggle = true,
  showNotifications = false,
  signInLink,
  ...props
}: TrimbleDeveloperHeaderProps) {
  const router = useRouter();
  const handleLogoClick = useCallback(() => {
    const href = props.logoHref;
    if (!href || isExternalUrl(href)) return;
    router.push(href);
  }, [props.logoHref, router]);
  useEffect(() => {
    // The global SiteNavigation is fixed-position and offsets `#page-content`
    // via the --nav-height CSS variable. TDHeader is sticky (not fixed), so
    // clear that offset to avoid a double gap. Restore the default on unmount.
    const pageContent = document.getElementById("page-content");
    pageContent?.style.setProperty("--nav-height", "0px");
    return () => {
      pageContent?.style.removeProperty("--nav-height");
    };
  }, []);
  useEffect(() => {
    // When the nav entry has light/dark disabled, the site must render in
    // light mode regardless of a previously stored preference or the OS
    // `prefers-color-scheme`. TDHeader's ModusWcThemeProvider re-derives the
    // mode from localStorage/system on every mount (see
    // modus-wc-theme-provider.js), so a stale "dark" choice from another
    // build would otherwise leak in. Force + watch `<html>` so it can't be
    // flipped back after the fact.
    if (showThemeToggle) return;
    const root = document.documentElement;
    const forceLight = () => {
      if (root.getAttribute("data-mode") === "light") return;
      const theme = (root.getAttribute("data-theme") ?? "modus-modern-light").replace(/-(light|dark)$/, "");
      root.setAttribute("data-theme", `${theme}-light`);
      root.setAttribute("data-mode", "light");
      root.classList.remove("dark");
      root.classList.add("light");
      try {
        const stored = localStorage.getItem("modus-theme-config");
        const parsed = stored ? JSON.parse(stored) : {};
        localStorage.setItem("modus-theme-config", JSON.stringify({
          ...parsed,
          theme,
          mode: "light"
        }));
      } catch {
        // localStorage unavailable (e.g. private browsing) — DOM attributes above still force light.
      }
    };
    forceLight();
    const observer = new MutationObserver(forceLight);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-theme", "data-mode"]
    });
    return () => observer.disconnect();
  }, [showThemeToggle]);

  // Same check LayoutComponent uses to determine logged-in state (see its
  // `tidConfig`/`useAuthTID`/`useAuthCheck` wiring): read the shared
  // LoginContext — populated by those hooks — and derive isAuthenticated
  // from it. The resulting logged-in state is passed straight through to
  // TDHeader below. LayoutComponent's own useAuthCheck() call is the single
  // source of truth for silent SSO on every route, including this one (it
  // ignores the sitewide nav's `disableLogin` flag specifically for TD
  // routes) — this component intentionally does NOT run its own competing
  // useAuthCheck(), since two independent callers racing to redirect via the
  // same shared auth-check state previously caused redirect-loop bugs.
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const isAuthenticated = isUserAuthenticated(loginState);
  const tidUser: TDHeaderProps["user"] | undefined = (() => {
    if (!isAuthenticated || !loginState?.user) return undefined;
    const name = [loginState.user.firstName, loginState.user.lastName].filter(Boolean).join(" ") || loginState.user.email || "";
    return {
      name,
      email: loginState.user.email,
      avatarUrl: loginState.user.img || undefined,
      manageTrimbleIdHref: `${myProfileBaseUrl}/`,
      initials: name
    };
  })();
  const handleSignOut = useCallback(() => {
    handleTIDSignOut(loginState?.idToken);
    loginDispatch?.({
      type: "LOG_OUT"
    });
  }, [loginState?.idToken, loginDispatch]);
  const shellClassName = ["td-header-shell", !showAssistant && "td-header-shell--hide-assistant", !showThemeToggle && "td-header-shell--hide-theme", !signInLink && "td-header-shell--hide-sign-in"].filter(Boolean).join(" ");
  return <div className={shellClassName}>
      {/*
        Modus icon fonts for nav labels (e.g. modus-icon-chat-centered-fill).
        Rendered as <link> tags — which React 19 hoists into <head> — rather
        than a CSS `@import url(...)`: production CSS chunking/inlining can
        merge TrimbleDeveloperNavigation.css with other stylesheets, moving
        the @import off the first line, which makes browsers silently ignore
        it (only breaks in production builds, not `next dev`).
       */}
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@trimble-oss/modus-icons-css@0.10.0/css/modus-icons-fill.min.css" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@trimble-oss/modus-icons-css@0.10.0/css/modus-icons-regular.min.css" />
      <TDHeader {...props} LinkComponent={Link} showNotifications={showNotifications} user={props.user ?? tidUser} onLogoClick={props.onLogoClick ?? handleLogoClick} onSignIn={props.onSignIn ?? handleTIDSignIn} onSignOut={props.onSignOut ?? handleSignOut} onAssistantClick={props.onAssistantClick ?? openAssistFlyout} />
    </div>;
}